import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * MasPatitas pack button.
 *
 * Without JavaScript the inner link is a cart permalink (`/cart/<id>:1,…?discount=CODE`), which
 * replaces the cart. Once this component loads it adds the pack lines to the existing cart instead,
 * applies the pack discount code next to any code already in the cart, and announces the update with
 * `CartLinesUpdateEvent` so the cart drawer, cart count and cart page refresh as after a normal add.
 * With no `data-lines` it only applies the code (lines already in the cart). Once the pack is in the
 * cart (`data-added`, also rendered by Liquid) a click opens the cart instead of adding it again.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} link - Permalink fallback and the clickable control.
 * @property {HTMLElement} [status] - Polite live region for errors.
 *
 * @extends {Component<Refs>}
 */
class MpPackButton extends Component {
  requiredRefs = ['link'];

  /** @type {AbortController | null} */
  #controller = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * @param {MouseEvent} event
   */
  handleClick = async (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    if (this.dataset.busy === 'true') return;

    // Already in the cart: show it instead of adding a second pack at full price
    if (this.dataset.added === 'true') {
      this.#showCart();
      return;
    }

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.dataset.busy = 'true';
    this.refs.link.setAttribute('aria-disabled', 'true');
    this.#setStatus('');

    const lines = this.#parseLines();
    const code = (this.dataset.discount || '').trim();
    const sections = this.#cartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    try {
      if (lines.length > 0) {
        const added = await fetch(Theme.routes.cart_add_url, {
          ...fetchConfig('json', { body: JSON.stringify({ items: lines }) }),
          signal,
        });
        const addData = await added.json();
        if (!added.ok || addData.status) throw new Error(addData.description || addData.message || 'Add failed');
      }

      const current = await fetch(`${Theme.routes.cart_url}.js`, { signal }).then((response) => response.json());
      /** @type {string[]} */
      const codes = (current.discount_codes || []).map((/** @type {{ code: string }} */ entry) => entry.code);
      if (code && !codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) codes.push(code);

      const updated = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify({ discount: codes.join(','), sections }) }),
        signal,
      });
      const cart = await updated.json();
      if (!updated.ok || cart.status) throw new Error(cart.description || cart.message || 'Discount failed');

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'mp-pack-button',
          sourceId: this.id,
          itemCount: lines.reduce((total, line) => total + line.quantity, 0),
          sections: cart.sections,
          didError: false,
        },
      });
      this.dataset.added = 'true';
      this.#keepFocusInCart();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.warn('[mp-pack-button]', error);
      this.#setStatus(this.dataset.errorText || '');
    } finally {
      if (!signal.aborted) {
        this.dataset.busy = 'false';
        this.refs.link.removeAttribute('aria-disabled');
      }
    }
  };

  /**
   * Reads `data-lines` ("variantId:quantity,variantId:quantity").
   * @returns {{ id: number, quantity: number }[]}
   */
  #parseLines() {
    const lines = [];
    for (const part of (this.dataset.lines || '').split(',')) {
      const [id, quantity] = part.split(':').map((value) => Number.parseInt(value, 10));
      if (id && id > 0) lines.push({ id, quantity: quantity && quantity > 0 ? quantity : 1 });
    }
    return lines;
  }

  /** @returns {string[]} The section IDs of the cart item lists on the page (drawer, cart page). */
  #cartSectionIds() {
    const ids = new Set();
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
    }
    return [...ids];
  }

  /**
   * Inside the cart drawer the offer is removed when the drawer re-renders, which would drop focus
   * to <body>. Once that happens, focus the checkout button (or the drawer) so keyboard users stay
   * in the cart.
   */
  #keepFocusInCart() {
    const drawer = this.closest('#cart-drawer');
    if (!drawer || !this.contains(document.activeElement)) return;

    const started = performance.now();
    const check = () => {
      if (this.isConnected && performance.now() - started < 3000) {
        requestAnimationFrame(check);
        return;
      }
      const active = document.activeElement;
      if (active && active !== document.body && drawer.contains(active)) return;
      const target =
        drawer.querySelector('.cart-drawer__summary [name="checkout"]') ||
        drawer.querySelector('[name="checkout"]') ||
        drawer.querySelector('dialog');
      if (target instanceof HTMLElement) target.focus();
    };
    requestAnimationFrame(check);
  }

  #showCart() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && 'open' in drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else {
      window.location.assign(Theme.routes.cart_url);
    }
  }

  /** @param {string} text */
  #setStatus(text) {
    if (this.refs.status) this.refs.status.textContent = text;
  }
}

if (!customElements.get('mp-pack-button')) {
  customElements.define('mp-pack-button', MpPackButton);
}
